import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HomeIcon, BookOpenIcon, UsersIcon, CalendarIcon, ShoppingBagIcon, HeartIcon, BookIcon, LayoutDashboardIcon, PlusIcon, PaletteIcon, TruckIcon, CompassIcon } from 'lucide-react';
import ServicesPopup from './ServicesPopup';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { usePlatform } from '../../context/PlatformContext';
interface MobileBottomBarProps {
  isReader: boolean;
  cartItemCount: number;
}
const MobileBottomBar: React.FC<MobileBottomBarProps> = ({
  isReader,
  cartItemCount
}) => {
  const dk = useDarkClasses();
  const {
    isAfterTide
  } = usePlatform();
  const location = useLocation();
  const navigate = useNavigate();
  const [showServicesPopup, setShowServicesPopup] = useState(false);
  const readerNavItems = [{
    path: '/',
    label: 'Home',
    icon: HomeIcon
  }, {
    path: '/browse',
    label: 'Browse',
    icon: CompassIcon
  }, {
    path: '/events',
    label: 'Events',
    icon: CalendarIcon
  }, {
    path: '/authors',
    label: 'Authors',
    icon: UsersIcon
  }, {
    path: '/my-bookshelf',
    label: 'Bookshelf',
    icon: BookIcon
  }];
  const vendorNavItems = [{
    path: '/vendors/dashboard',
    label: 'Dashboard',
    icon: LayoutDashboardIcon
  }, {
    path: '/manage-listings',
    label: 'Listings',
    icon: BookOpenIcon
  }, {
    path: '/vendors/manage',
    label: 'Create',
    icon: PlusIcon,
    isCreate: true
  }, {
    path: '/orders',
    label: 'Orders',
    icon: TruckIcon
  }, {
    action: () => setShowServicesPopup(true),
    label: 'Services',
    icon: PaletteIcon
  }];
  const navItems = isReader ? readerNavItems : vendorNavItems;
  const getActiveColor = (isActive: boolean) => {
    if (!isActive) {
      return isAfterTide ? 'text-[#a0a0b0]' : dk.isDark ? 'text-gray-400' : 'text-gray-500';
    }
    return isAfterTide ? 'text-[#e2b04a]' : isReader ? 'text-indigo-600' : 'text-purple-700';
  };
  return <>
      <div className={`fixed bottom-0 left-0 right-0 border-t z-40 md:hidden transition-all duration-300 ${isAfterTide ? 'bg-[#1a1a2e] border-[#16213e]' : dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-2721" data-mp="2b0syyynvyxd0">
        <div className={`grid grid-cols-5 h-[calc(64px+env(safe-area-inset-bottom))] items-start pt-2 pb-[env(safe-area-inset-bottom)] ${isAfterTide ? 'bg-[#1a1a2e]' : 'bg-[#111827]'}`} data-id="element-2722" data-mp="1n9xb2cmjwmb1">
          {navItems.map((item, index) => {
          const isActive = item.path ? location.pathname === item.path || item.path !== '/' && location.pathname.startsWith(item.path) : location.pathname.startsWith('/services') || location.pathname.startsWith('/talent') || location.pathname.startsWith('/creative');
          // Vendor "Create" button — elevated center action
          if (item.isCreate && item.path) {
            return <Link key={item.path} to={item.path} className="flex flex-col items-center justify-center -mt-3 tap-highlight" data-id="element-2723" data-mp="382uf0avxn849">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all active:scale-90 ${isActive ? isAfterTide ? 'bg-[#c77dba] shadow-[#c77dba]/30' : 'bg-purple-600 shadow-purple-500/30' : isAfterTide ? 'bg-[#c77dba] shadow-[#c77dba]/20' : dk.isDark ? 'bg-purple-600 shadow-purple-500/20' : 'bg-purple-600 shadow-purple-500/25'}`} data-id="element-2724" data-mp="2z48yela0u364">
                    <PlusIcon className={`h-6 w-6 ${isAfterTide ? 'text-[#1a1a2e]' : 'text-white'}`} strokeWidth={2.5} data-id="element-2725" data-mp="nd50y031zqcm" />
                  </div>
                  <span className={`text-[10px] font-semibold mt-1 ${isActive ? isAfterTide ? 'text-[#c77dba]' : 'text-purple-600' : isAfterTide ? 'text-[#a0a0b0]' : dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2726" data-mp="qg2rnb72wo27">
                    {item.label}
                  </span>
                </Link>;
          }
          // Action button (Services popup)
          if (item.action) {
            return <button key={index} onClick={item.action} className={`flex flex-col items-center justify-center px-2 touch-target tap-highlight active:scale-95 transition-transform ${getActiveColor(isActive)}`} data-id="element-2727" data-mp="cgjx5l76xb8d">
                  <item.icon className="h-6 w-6 mb-0.5" data-id="element-2728" data-mp="3kus8lghkzldb" />
                  <span className="text-[10px] font-medium" data-id="element-2729" data-mp="3kxeoc3eml76m">{item.label}</span>
                </button>;
          }
          // Standard nav link
          return <Link key={item.path} to={item.path!} className={`flex flex-col items-center justify-center px-2 touch-target tap-highlight active:scale-95 transition-transform ${getActiveColor(isActive)}`} data-id="element-2730" data-mp="368v5ussnxbys" data-mpi={index}>
                <item.icon className="h-6 w-6 mb-0.5" data-id="element-2731" data-mp="3b6mwzklhgu1x" />
                <span className="text-[10px] font-medium" data-id="element-2732" data-mp="3ba2awk3f7x1r">{item.label}</span>
              </Link>;
        })}
        </div>
      </div>
      <ServicesPopup isOpen={showServicesPopup} onClose={() => setShowServicesPopup(false)} data-id="element-2733" data-mp="3k7cquir6oad0" />
    </>;
};
export default MobileBottomBar;